
import React from 'react';
import { Badge } from "@/components/ui/badge";
import { motion, useAnimationControls } from "framer-motion";
import { TikTokIcon } from "@/components/icons/TikTokIcon";
import { ChallengeInfoCards } from './ChallengeInfoCards';
import { ChallengeActions } from './ChallengeActions';

interface ChallengeHeroContentProps {
  title: string;
  timeLeft: {
    days: number;
    hours: number;
    minutes: number;
  };
  cashPrize: string;
  cpmPayout: string;
  isCompleted?: boolean;
  completionDate?: string;
}

const fadeIn = {
  hidden: {
    opacity: 0,
    y: 10
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3
    }
  }
};

export const ChallengeHeroContent: React.FC<ChallengeHeroContentProps> = ({
  title,
  timeLeft,
  cashPrize,
  cpmPayout,
  isCompleted = false,
  completionDate
}) => {
  const controls = useAnimationControls();
  const isAnimatedRef = React.useRef(false);

  React.useEffect(() => {
    if (!isAnimatedRef.current) {
      controls.start("visible");
      isAnimatedRef.current = true;
    }
  }, [controls]);

  return (
    <motion.div 
      className="space-y-6" 
      initial="hidden" 
      animate={controls} 
      variants={{
        visible: {
          transition: {
            staggerChildren: 0.05
          }
        }
      }}
    >            
      <motion.div variants={fadeIn} className="inline-block">
        <Badge className="bg-[#000000] hover:bg-[#000000]/90 text-white px-3 py-1.5 rounded-full text-xs font-medium inline-flex items-center gap-1.5">
          <TikTokIcon size={14} />
          <span>TikTok</span>
        </Badge>
        
        {isCompleted && (
          <Badge className="bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100 ml-2 px-3 py-1.5 rounded-full text-xs font-medium">
            Challenge terminé
          </Badge>
        )}
      </motion.div>
      
      <motion.h1 
        className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight" 
        variants={fadeIn}
      >
        <span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-500 to-violet-400">
          🎬 Challenge Créateurs : L'amour c'est surcoté
        </span>
      </motion.h1>
      
      <motion.p 
        className="text-sm md:text-base text-muted-foreground max-w-lg" 
        variants={fadeIn}
      >
        <strong>Fais exploser ta visibilité sur TikTok avec StudioCanal et ReCut !</strong> 
        Crée des shorts viraux à partir d'extraits exclusifs du film événement « L'amour c'est surcoté » avec Hakim Jemili et Laura Felpin. Gagne en notoriété, fais monter ton nombre de vues et monétise enfin ton talent de remixeur !
      </motion.p>
      
      <motion.div variants={fadeIn}>
        <ChallengeInfoCards 
          cashPrize={cashPrize}
          cpmPayout={cpmPayout}
          timeLeft={timeLeft}
          isCompleted={isCompleted}
          completionDate={completionDate}
        />
      </motion.div>
      
      <motion.div variants={fadeIn}>
        <ChallengeActions isCompleted={isCompleted} />
      </motion.div>
    </motion.div>
  );
};
